import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AdminLayout from "./AdminLayout";

export default function AdminDashboard() {
  const { admin } = useAuth();
  const role = admin?.role || "admin";
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [metrics, setMetrics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    users: 0,
  });

  const [recent, setRecent] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // -------------------------------
  // FETCH METRICS
  // -------------------------------
  useEffect(() => {
    async function loadMetrics() {
      try {
        const token = localStorage.getItem("adminToken");

        const res = await fetch(`${API}/api/admin/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          console.error("Failed to fetch metrics", res.status);
          return;
        }

        const data = await res.json();

        setMetrics(data.metrics || {});
        setRecent(data.recent || []);
        setAgents(data.agents || []);
      } catch (err) {
        console.error("Metrics Load Error:", err);
      } finally {
        setLoading(false);
      }
    }

    loadMetrics();
  }, [API]);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Application Received, Pending for Review": {
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: "⏳",
      },
      "Additional Information Requested": {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: "📝",
      },
      "Additional Information Received": {
        color: "bg-indigo-50 text-indigo-700 border-indigo-200",
        icon: "✓",
      },
      "Submission Done, Awaiting Visa Status": {
        color: "bg-purple-50 text-purple-700 border-purple-200",
        icon: "⏱️",
      },
      "Visa Approved": {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: "✅",
      },
      "Visa Rejected": {
        color: "bg-red-50 text-red-700 border-red-200",
        icon: "❌",
      },
      draft: {
        color: "bg-slate-50 text-slate-600 border-slate-200",
        icon: "📄",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${config.color}`}
      >
        <span>{config.icon}</span>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-3 border-red-600 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-sm text-gray-600 font-medium">
              Loading dashboard...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* HEADER */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            </div>
            {role === "admin" && (
              <button
                onClick={() => navigate("/admin/applications")}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow transition-all duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                View All Applications
              </button>
            )}
          </div>
        </div>

        {/* METRIC CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <div className="bg-white hover:bg-blue-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Total Applications
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {metrics.total_applications || 0}
                </p>
              </div>
              <div className="text-2xl">📊</div>
            </div>
          </div>

          <div className="bg-white hover:bg-amber-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Pending
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {metrics.pending_submission || 0}
                </p>
              </div>
              <div className="text-2xl">⏳</div>
            </div>
          </div>

          <div className="bg-white hover:bg-emerald-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Approved
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {metrics.approved || 0}
                </p>
              </div>
              <div className="text-2xl">✅</div>
            </div>
          </div>

          <div className="bg-white hover:bg-red-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Rejected
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {metrics.rejected || 0}
                </p>
              </div>
              <div className="text-2xl">❌</div>
            </div>
          </div>

          <div className="bg-white hover:bg-purple-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Total Agents
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {agents.length || 0}
                </p>
              </div>
              <div className="text-2xl">👥</div>
            </div>
          </div>
        </div>

        {/* RECENT APPLICATIONS */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                Recent Applications
              </h3>
              {role === "admin" && (
                <button
                  onClick={() => navigate("/admin/applications")}
                  className="text-sm font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  View All
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {recent.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Application ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Agent
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Visa Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                    {role === "admin" || role === "operator" ? (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {recent.slice(0, 5).map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(app.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-700">
                          {app.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {app.agent || "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {app.visa_category || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {app.visa_type_name || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>
                      {role === "admin" || role === "operator" ?  (
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() =>
                                navigate(`/admin/applications/${app.id}`)
                              }
                              className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                              title="View Application"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      ): null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                📋
              </div>
              <h4 className="text-sm text-gray-900 font-semibold mb-1">
                No applications yet
              </h4>
              <p className="text-xs text-gray-500">
                Applications will appear here once submitted
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}